// @flow

import * as mediator from "@tvg/mediator-classic/src";
import { createStore, applyMiddleware } from "redux";
import type { Store } from "redux";
import React, { Component } from "react";
import cookie from "react-cookie";
import _, {noop} from "lodash";
import tvgConf from "@tvg/conf";
import { doLoginAction } from "./actions";
import LogoutAction from "./actions/logout";
import ProfileAction from "./actions/profile";
import IdRequestAction from "./actions/idRequest";
import filterPrefsAction from "./actions/filterPrefs";

import reducers from "./reducers";
import extReducer from "./externalReducer";
import extActions from "./externalActions";
import CONSTANTS from "./constants";
import type { ProfileResponse } from "./actions/profile";
import type { idResponse } from "./actions/idRequest";
import type { LogoutResponse } from "./actions/logout";
import type {
  ResponseObject,
  Payload,
  StorageInterface
} from "./Types.js.flow";

import Login from "./LoginComponent/index.jsx";
import CustomModal from "./LoginComponent/LoginBlock/CustomModal/index.jsx";

import Utils from "./utils.jsx";
import VoidStorage from "./Storage";
import TimeDiff from "./utils/timeDiff";


const getStorage = (): StorageInterface => {
  if (typeof window === "object" && !!window.sessionStorage) {
    return window.sessionStorage;
  }
  return new VoidStorage();
};

const signalErrorPreferences = (storage, error): void => {
  storage.getItem("favoriteTracks") && storage.removeItem("favoriteTracks");
  storage.getItem("raceFilters") && storage.removeItem("raceFilters");
  mediator.dispatch("FAVORITE_TRACKS_ERROR", { e: error });
};

const closeMobileLoginModalEvent = (isMobile: boolean): void => {
  isMobile &&
    mediator.dispatch("HEADER_MOBILE_TOGGLE_SECTION", {
      action: "close",
      section: "Login"
    });
};

const cookieService = (
  cookieName: string,
  actionType: boolean,
  cookieValue?: string
) => {
  const domain = window.location.hostname;
  const domainForCookie = domain.substring(domain.indexOf("."));

  if (cookie.load(cookieName, { domain: domainForCookie })) {
    cookie.remove(cookieName, { domain: domainForCookie });
  }

  if (actionType) {
    cookie.save(cookieName, cookieValue, { domain: domainForCookie });
  }
};

const getWindowSize = (): boolean =>
  typeof window !== "undefined" && Number(window.innerWidth) <= Number(750);

export class TVGLogin extends Component {
  store: Store<*, *>;

  constructor(props) {
    super(props);
    this.store = createStore(reducers);
    this.storage = getStorage();

    _.bindAll(this, [
      "triggerLogin",
      "triggerLogout",
      "validateSession",
      "watchSession",
      "requestProfile",
      "openLoginModal",
      "closeLoginModal",
      "closeLoginModalCallBack",
      "openSessionExpiredModal",
      "requestId",
      "signalNewRaceFilters",
      "signalPreferencesLoad",
      "loadPreferences",
      "requestCMSMsgs",
      "getCMSMsgs"
    ]);

  }

  componentDidMount(): void {
    mediator.subscribe(CONSTANTS["TVG_LOGIN:DO_LOGIN"], this.triggerLogin);
    mediator.subscribe(CONSTANTS["TVG_LOGIN:DO_LOGOUT"], this.triggerLogout);
    mediator.subscribe(
      CONSTANTS["TVG_LOGIN:OPEN_LOGIN_MODAL"],
      this.openLoginModal
    );
    mediator.subscribe(
      CONSTANTS["TVG_LOGIN:CLOSE_LOGIN_MODAL"],
      this.closeLoginModal
    );
    mediator.subscribe(CONSTANTS["TVG_LOGIN:VALIDATE_SESSION"], () =>
      this.validateSession(true)
    );

    this.getCMSMsgs();

    this.watchSession();
  }

  // Search the cmsCache in window and save messages in this.cmsMsgs
  getCMSMsgs(logged: boolean = this.store.getState().logged) {
    if (!logged) {
      typeof window !== "undefined" && _.get(window, "cmsCache.Login")
        ? (this.cmsMsgs = _.get(window, "cmsCache.Login"))
        : this.requestCMSMsgs();
    }
  }

  // Make request to CMS and save messages in this.cmsMsgs
  requestCMSMsgs(): void {
    tvgConf()
      .getMessages(["Login"])
      .then(response => {
        this.cmsMsgs = response;
      })
      .catch(() => {
        this.cmsMsgs = {
          messages: {
            LoginAware:
              "AWARE – Always Wager Responsibly National Gambling Helpline: (800) 522-4700."
          }
        };
      });
  }

  signalNewRaceFilters = (data: Object) => {
    if (_.get(data, "status") === "success" && !!_.get(data, "raceFilters")) {
      _.attempt(() => {
        this.storage.setItem("raceFilters", data.raceFilters);
      });
      mediator.dispatch("LOAD_FILTERS:UPDATE", {
        raceFilters: data.raceFilters
      });
    } else {
      mediator.dispatch("LOAD_FILTERS:ERROR", { e: "NO RACE FILTERS" });
    }
  };

  signalPreferencesLoad = () => {
    mediator.dispatch("LOAD_PREFERENCES", {});
  };

  triggerLogin(payload: Payload, dropdown: boolean): Promise<ResponseObject> {
    TimeDiff.markStart(CONSTANTS.METRIC_USER_LOGIN);
    const newPayload = Object.assign({ messages: this.cmsMsgs }, payload);
    return doLoginAction(newPayload).then((response: ResponseObject) => {
      const type = response.error
        ? CONSTANTS.SET_SESSION_ERROR
        : CONSTANTS.SET_SESSION_SUCCESS;

      if (response.success) {
        const accoundId =
          _.get(response.success, "data.userDetails.accountNumber") ||
          _.get(response.success, "data.user.accountNumber");

        response.success.fromLogin = dropdown || false;

        // the pin is being stored in the user details
        // so it can be sent to the ios app keychain for touch id
        // no security flaw here :)
        response.success.data.userDetails.pin = payload.pin;

        this.profile = response.success.data.userDetails.profile;

        cookieService("wp", true, this.profile);

        _.attempt(() => {
          this.storage.setItem("userId", accoundId);
        });

        // request filter preferences
        this.loadPreferences(accoundId);
      } else {
        // always alert apps about preferences load
        this.signalPreferencesLoad();
      }

      if (
        payload &&
        payload.callback &&
        typeof payload.callback === "function"
      ) {
        payload.callback(response.error, response.success);
      }

      this.store.dispatch({
        type,
        payload: Object.assign(
          {},
          { userLogin: !payload.notFromLogin },
          response.error || response.success
        )
      });

      return response;
    });
  }

  triggerLogout(payload: {
    callback?: {
      success: ?{ status: string },
      error: ?{ status: string, message: string }
    },
    sessionExpired?: boolean
  }): Promise<LogoutResponse> {
    return LogoutAction().then((response: LogoutResponse) => {
      const type = response.success ? CONSTANTS.CLEAR_SESSION : "";
      if (
        payload &&
        payload.callback &&
        typeof payload.callback === "function"
      ) {
        payload.callback(response.error, response.success);
      }

      if (payload && payload.sessionExpired) {
        this.openSessionExpiredModal();
      }

      if (response && response.success && typeof window !== "undefined") {
        cookieService("wp", false);

        _.attempt(() => {
          this.storage.getItem("userId") && this.storage.removeItem("userId");
          this.storage.getItem("isFirstTouch") &&
            this.storage.removeItem("isFirstTouch");
          this.storage.getItem("raceFilters") &&
            this.storage.removeItem("raceFilters");
          this.storage.getItem("favoriteTracks") &&
            this.storage.removeItem("favoriteTracks");
        });
      }

      if (type) {
        this.store.dispatch({
          type,
          payload: Object.assign(
            {},
            { userLogout: true },
            response.error || response.success
          )
        });
      }

      return response;
    });
  }
  watchSession(): number {
    this.validateSession(true);
    return setInterval(() => this.validateSession(false), 60000);
  }

  validateSession(
    withPreferences: boolean = false,
    logged: boolean = this.store.getState().logged
  ) {
    const userId = this.storage.getItem("userId");
    if (!logged && userId) {
      this.requestProfile(userId, withPreferences);
    } else if (!logged && !userId) {
      this.requestId(withPreferences);
    }
  }

  requestId(withPreferences: boolean) {
    return IdRequestAction().then((response: idResponse) => {
      if (response.success && response.success.data) {
        const userId = response.success.data.response.data.accountNumber;

        _.attempt(() => {
          this.storage.setItem("userId", userId);
        });

        this.requestProfile(userId, withPreferences);
      } else if (response.error) {
        const type = CONSTANTS.CLEAR_SESSION;
        if (type) {
          this.store.dispatch({
            type,
            payload: { userLogout: false }
          });
        }
        // always alert apps about preferences load
        this.signalPreferencesLoad();
      }
    });
  }

  requestProfile(
    userId: string,
    withPreferences: boolean
  ): Promise<ProfileResponse> {
    return ProfileAction(userId).then((response: ProfileResponse) => {
      const type = response.error
        ? CONSTANTS.CLEAR_SESSION
        : CONSTANTS.SET_USER;
      if (type) {
        this.store.dispatch({
          type,
          payload:
            response.success && response.success.data
              ? response.success.data
              : {}
        });
      }

      if (response.success && response.success.data) {
        const accoundId =
          _.get(response.success, "data.userDetails.accountNumber") ||
          _.get(response.success, "data.user.accountNumber");
        cookieService("wp", true, response.success.data.user.profile);

        // request preferences and favorite tracks
        if (withPreferences) {
          // request filter preferences
          this.loadPreferences(accoundId);
        }
      } else {
        // always alert apps about preferences load
        this.signalPreferencesLoad();
      }

      return response;
    });
  }

  modalLogin: Object;
  modalSessionExpired: Object;
  profile: string;
  cmsMsgs: Object;
  isMobile: boolean;
  storage: StorageInterface;

  loadPreferences(accountNumber: string) {
    return Promise.all([filterPrefsAction(accountNumber)])
      .then(results => {
        this.signalNewRaceFilters(results[0]);
      })
      .catch(e => signalErrorPreferences(this.storage, e))
      .then(this.signalPreferencesLoad);
  }

  closeLoginModalCallBack() {
    if (this.modalLogin && this.modalLogin.close) {
      this.modalLogin.close();
    }
    closeMobileLoginModalEvent(this.isMobile);
    setTimeout(() => {
      this.modalLogin = null;
    }, 20);
  }

  openLoginModal(payload: Object): void {
    if (this.modalLogin && !this.modalLogin.state.isModalOpen) {
      this.modalLogin.open();
    } else {
      this.isMobile = getWindowSize() || payload.mobile;
      this.modalLogin = Utils.openModal(
        "TVG_LOGIN_MODAL",
        <Login
          cmsMsgs={this.cmsMsgs}
          mobile={this.isMobile}
          signupToShow={payload.signupToShow}
          callback={payload.callback}
          triggerLogin={this.triggerLogin}
          closeModal={this.closeLoginModalCallBack}
        />,
        instance => {
          this.modalLogin = instance;
          this.modalLogin.open();
        }
      );
    }
  }

  closeLoginModal(): void {
    if (this.modalLogin && this.modalLogin.state.isModalOpen) {
      this.modalLogin.close();
      setTimeout(() => {
        this.modalLogin = null;
      }, 20);
    }
  }

  openSessionExpiredModal() {
    this.modalSessionExpired = Utils.openModal(
      "TVG_LOGIN_SESSION_EXPIRED",
      <CustomModal
        type="sessionExpired"
        cmsMsgs={this.cmsMsgs}
        closeModal={() => {
          if (this.modalSessionExpired && this.modalSessionExpired.close) {
            this.modalSessionExpired.close();
          }
        }}
      />,
      true
    );

    if (this.modalSessionExpired && this.modalSessionExpired.open) {
      this.modalSessionExpired.open();
    }
  }

  render() {
    return <Login
        dropdown
        cmsMsgs={this.cmsMsgs}
        triggerLogin={this.triggerLogin}
        closeDropdownCallback={noop}
      />
    };
}

export const externalReducer = extReducer;

export const injectMediatorActions = extActions;

export default TVGLogin;
